<template>
  <Transition name="difficulty-modal">
    <div
      v-if="visible"
      class="difficulty-backdrop"
      data-testid="bot-difficulty-selector"
      @click.self="$emit('close')"
      @keydown.escape="$emit('close')"
    >
      <div
        class="difficulty-modal"
        role="dialog"
        :aria-label="STRINGS.aria.labels.selectBotDifficulty"
        aria-modal="true"
        tabindex="-1"
        ref="modalRef"
      >
        <div class="difficulty-modal__header">
          <h2 class="difficulty-modal__title">{{ STRINGS.ui.headings.soloGame }}</h2>
          <p class="difficulty-modal__subtitle">{{ STRINGS.ui.headings.chooseDifficulty }}</p>
        </div>

        <div class="difficulty-modal__stack">
          <button
            v-for="option in difficulties"
            :key="option.difficulty"
            class="difficulty-card"
            :class="`difficulty-card--${option.difficulty.toLowerCase()}`"
            :aria-label="option.ariaLabel"
            :data-testid="option.testId"
            @click="$emit('select', option.difficulty)"
          >
            <span
              class="difficulty-card__icon"
              :class="`difficulty-card__icon--${option.difficulty.toLowerCase()}`"
            >
              <AppIcon name="bot" :size="20" color="currentColor" />
            </span>
            <span class="difficulty-card__info">
              <span class="difficulty-card__label">{{ option.label }}</span>
              <span class="difficulty-card__desc">{{ option.desc }}</span>
            </span>
            <span class="difficulty-card__arrow" aria-hidden="true">&#x2192;</span>
          </button>
        </div>

        <p v-if="guest" class="difficulty-modal__legal" data-testid="guest-legal-agreement">
          {{ STRINGS.legal.guest.agreement }}
          <button
            class="difficulty-modal__legal-link"
            data-testid="guest-legal-terms-link"
            @click="$emit('navigate', 'terms')"
            type="button"
          >
            {{ STRINGS.legal.guest.termsLink }}
          </button>
          {{ STRINGS.legal.guest.and }}
          <button
            class="difficulty-modal__legal-link"
            data-testid="guest-legal-privacy-link"
            @click="$emit('navigate', 'privacy')"
            type="button"
          >
            {{ STRINGS.legal.guest.privacyLink }}</button
          >.
        </p>

        <button
          class="difficulty-modal__close"
          data-testid="difficulty-close-button"
          :aria-label="STRINGS.aria.labels.closeDifficultySelector"
          @click="$emit('close')"
        >
          {{ STRINGS.ui.buttons.cancel }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import AppIcon from './ui/icons/AppIcon.vue';
import STRINGS from '@/constants/strings';

const props = defineProps<{
  visible: boolean;
  guest?: boolean;
}>();

defineEmits<{
  select: [difficulty: 'Easy' | 'Medium' | 'Hard'];
  close: [];
  navigate: [target: 'terms' | 'privacy'];
}>();

const modalRef = ref<HTMLElement | null>(null);

watch(
  () => props.visible,
  async isVisible => {
    if (isVisible) {
      await nextTick();
      modalRef.value?.focus();
    }
  }
);

const difficulties = [
  {
    difficulty: 'Easy' as const,
    label: STRINGS.ui.buttons.easy,
    desc: STRINGS.ui.descriptions.easyBot,
    ariaLabel: STRINGS.aria.labels.easyBot,
    testId: 'bot-easy-button',
  },
  {
    difficulty: 'Medium' as const,
    label: STRINGS.ui.buttons.medium,
    desc: STRINGS.ui.descriptions.mediumBot,
    ariaLabel: STRINGS.aria.labels.mediumBot,
    testId: 'bot-medium-button',
  },
  {
    difficulty: 'Hard' as const,
    label: STRINGS.ui.buttons.hard,
    desc: STRINGS.ui.descriptions.hardBot,
    ariaLabel: STRINGS.aria.labels.hardBot,
    testId: 'bot-hard-button',
  },
];
</script>

<style scoped>
.difficulty-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 12, 8, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 1rem;
}

.difficulty-modal {
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-card);
  padding: 2rem 1.75rem;
  max-width: 380px;
  width: 100%;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(201, 168, 76, 0.06);
  text-align: center;
  outline: none;
}

.difficulty-modal__header {
  margin-bottom: 0.5rem;
}

.difficulty-modal__title {
  font-family: var(--font-display);
  font-size: 1.375rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: var(--color-arena-text-bright);
  margin: 0;
}

.difficulty-modal__subtitle {
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  margin: 0.375rem 0 0;
  letter-spacing: 0.02em;
}

.difficulty-modal__stack {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 1.5rem;
}

/* --- Difficulty card --- */
.difficulty-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: rgba(26, 22, 18, 0.6);
  border: 1.5px solid var(--color-arena-border);
  border-radius: 0.75rem;
  cursor: pointer;
  transition:
    transform 200ms ease-out,
    background-color 200ms ease-out,
    border-color 200ms ease-out;
  text-align: left;
  position: relative;
  overflow: hidden;
  font-family: var(--font-sans);
  color: var(--color-arena-text-bright);
}

.difficulty-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 3px 0 0 3px;
  transition: opacity 200ms;
  opacity: 0;
}

.difficulty-card:hover {
  background: rgba(26, 22, 18, 0.9);
  transform: translateX(2px);
}

.difficulty-card:hover::before {
  opacity: 1;
}

.difficulty-card:active {
  transform: translateX(2px) scale(0.99);
}

.difficulty-card:focus-visible {
  outline: 3px solid var(--color-primary-600);
  outline-offset: 2px;
  box-shadow:
    0 0 0 1px #ffffff,
    0 0 0 4px var(--color-primary-500);
}

/* Per-difficulty accents -- steel-blue family (derived from energy-secondary) */
.difficulty-card--easy::before {
  background: #6b9bc4;
}
.difficulty-card--easy:hover {
  border-color: rgba(107, 155, 196, 0.3);
}

.difficulty-card--medium::before {
  background: var(--color-energy-secondary);
}
.difficulty-card--medium:hover {
  border-color: rgba(91, 127, 164, 0.3);
}

.difficulty-card--hard::before {
  background: #4a6583;
}
.difficulty-card--hard:hover {
  border-color: rgba(74, 101, 131, 0.35);
}

/* Icon container */
.difficulty-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.difficulty-card__icon--easy {
  background: rgba(107, 155, 196, 0.12);
  border: 1px solid rgba(107, 155, 196, 0.22);
  color: #6b9bc4;
}

.difficulty-card__icon--medium {
  background: rgba(91, 127, 164, 0.12);
  border: 1px solid rgba(91, 127, 164, 0.22);
  color: var(--color-energy-secondary);
}

.difficulty-card__icon--hard {
  background: rgba(74, 101, 131, 0.15);
  border: 1px solid rgba(74, 101, 131, 0.25);
  color: #4a6583;
}

.difficulty-card__info {
  flex: 1;
  min-width: 0;
}

.difficulty-card__label {
  display: block;
  font-family: var(--font-display);
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-bright);
}

.difficulty-card__desc {
  display: block;
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  margin-top: 0.125rem;
  line-height: 1.4;
}

.difficulty-card__arrow {
  color: var(--color-arena-text-muted);
  font-size: 0.875rem;
  transition:
    transform 200ms,
    color 200ms;
  flex-shrink: 0;
}

.difficulty-card:hover .difficulty-card__arrow {
  transform: translateX(3px);
  color: var(--color-arena-text-bright);
}

/* Legal agreement (guest only) */
.difficulty-modal__legal {
  font-size: 0.6875rem;
  line-height: 1.5;
  color: var(--color-arena-text-dim, #6b5f4e);
  text-align: center;
  margin: 1.25rem 0 0;
}

.difficulty-modal__legal-link {
  color: var(--color-arena-text-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 150ms ease;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
}

.difficulty-modal__legal-link:hover {
  color: var(--color-arena-text-bright);
}

/* Close link */
.difficulty-modal__close {
  display: inline-block;
  margin-top: 1.25rem;
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  cursor: pointer;
  background: none;
  border: none;
  font-family: var(--font-sans);
  transition: color 150ms;
  letter-spacing: 0.02em;
}

.difficulty-modal__close:hover {
  color: var(--color-arena-text-bright);
}

/* Transition */
.difficulty-modal-enter-active {
  transition: opacity 0.25s ease-out;
}

.difficulty-modal-enter-active .difficulty-modal {
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.25s ease-out;
}

.difficulty-modal-leave-active {
  transition: opacity 0.15s ease-in;
}

.difficulty-modal-enter-from {
  opacity: 0;
}

.difficulty-modal-enter-from .difficulty-modal {
  transform: translateY(24px) scale(0.95);
  opacity: 0;
}

.difficulty-modal-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .difficulty-modal-enter-active,
  .difficulty-modal-enter-active .difficulty-modal,
  .difficulty-modal-leave-active {
    transition: none;
  }

  .difficulty-card:hover {
    transform: none;
  }

  .difficulty-card:active {
    transform: none;
  }
}
</style>
